import DocumentIcon from "../assets/Icons/svg/documentIcon";
import PdfIcon from "../assets/Icons/svg/pdfIcon";
import ReceivedIcon from "../assets/Icons/svg/receivedIcon";
import SendIcon from "../assets/Icons/svg/sendIcon";
import { UserModel } from "../models";

export function getFormattedDate(dateString: Date | string) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const date = new Date(dateString);
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let daySuffix;
  if (day >= 11 && day <= 13) {
    daySuffix = "th";
  } else {
    switch (day % 10) {
      case 1:
        daySuffix = "st";
        break;
      case 2:
        daySuffix = "nd";
        break;
      case 3:
        daySuffix = "rd";
        break;
      default:
        daySuffix = "th";
    }
  }

  const formattedDate = `${month} ${day}${daySuffix} ${year}`;
  return formattedDate;
}

export function getFormattedDateTime(dateString: Date | string) {
  const date = new Date(dateString);

  // Format the date
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const formattedDate = `${month}/${day}/${year}`;

  // Format the time
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedTime = `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  return [formattedDate, formattedTime];
}

export function isValidEmail(email: string | null) {
  let regex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
  if (!email) {
    return false;
  }
  return regex.test(email);
}

export function getFullName(user: Pick<UserModel, "firstName" | "lastName">) {
  let fullName = `${user?.firstName} ${user?.lastName}`;
  return fullName;
}

const colors = [
  "#101828",
  "#007CFF",
  "#750b1c",
  "#18468F",
  "#e3008c",
  "#5c2e91",
  "#986f0b",
];

export const getColor = (name: string) => {
  return colors[name?.length % colors.length];
};

// decodeHtmlEntities lives in its own module so it can be unit-tested in isolation
// (and imported without pulling in the rest of this utils barrel). Re-exported here
// for existing `from "../../../utils"` consumers.
export { decodeHtmlEntities } from "./decodeHtmlEntities";

export function getSplitDocumentName(name: string) {
  if (!name) return "";

  if (name.endsWith(".doc")) {
    return name.split(".doc")[0];
  }
  else if (name.endsWith(".docx")) {
    return name.split(".docx")[0];
  }
  else if (name.endsWith(".pdf")) {
    return name.split(".pdf")[0];
  }

  return name;
}

type MRUSortable = { lastActivityDate?: Date | string; lastUpdatedDate?: Date | string };

export function getSortedMRU<T extends MRUSortable>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aUpdated = a.lastUpdatedDate ? new Date(a.lastUpdatedDate as string).getTime() : 0;
    const aActivity = a.lastActivityDate ? new Date(a.lastActivityDate as string).getTime() : 0;
    const bUpdated = b.lastUpdatedDate ? new Date(b.lastUpdatedDate as string).getTime() : 0;
    const bActivity = b.lastActivityDate ? new Date(b.lastActivityDate as string).getTime() : 0;
    return Math.max(bUpdated, bActivity) - Math.max(aUpdated, aActivity);
  });
}

export function getIcon(fileType: string, fileFormat?: string) {
  if (fileType === "sent") {
    return <SendIcon />;
  } else if (fileType === "received") {
    return <ReceivedIcon />;
  } else {
    return fileFormat === "pdf" ? <PdfIcon /> : <DocumentIcon />;
  }
}

export function isNearOneMinute(dateTime: Date): boolean {
  const currentTime = new Date();
  const timeDifference = Math.abs(currentTime.getTime() - dateTime.getTime());
  const oneMinuteInMilliseconds = 2 * 60 * 1000;
  return timeDifference <= oneMinuteInMilliseconds;
}

export function getUrlParams(url: string) : URLSearchParams {
  try {
    return new URLSearchParams(new URL(url).search);
  } catch (error) {
    console.error('Invalid URL:', url);
    return new URLSearchParams();
  }
};

export function getStoredRedirectId(): string | null {
  const storedLink = localStorage.getItem('LINK') ?? window.location.href;
  const urlParams = getUrlParams(storedLink);
  return urlParams.get('redirect_id');
};

export function getStoredLink(): string | null {
  const link = localStorage.getItem('LINK')

  if (!link) return null;

  const urlObject = new URL(link);
  // Keep the query string: deep links like /import?source=outlook&messageId=...
  // are meaningless without their parameters.
  return urlObject.pathname + urlObject.search;
};

// Compact currency formatting for pipeline/summary displays. Lifted from the
// byte-identical inline copies in ProjectDataButtonStack / FunctionalityButtonStack
// (DEX-648) so there is one source of truth. Behavior is unchanged.
export function formatMoney(value: any) {
  if (value === null || value === undefined) return "$0";

  const absValue = Math.abs(value);
  let formattedValue;

  if (absValue >= 1e12) {
    formattedValue = (value / 1e12).toFixed(1) + "T"; // Trillions
  } else if (absValue >= 1e9) {
    formattedValue = (value / 1e9).toFixed(1) + "B"; // Billions
  } else if (absValue >= 1e6) {
    formattedValue = (value / 1e6).toFixed(1) + "M"; // Millions
  } else if (absValue >= 1e3) {
    formattedValue = (value / 1e3).toFixed(1) + "K"; // Thousands
  } else {
    formattedValue = value.toFixed(2); // Less than a thousand
  }

  return `$${formattedValue}`;
}

export function formatFullMoney(value: any) {
  if (value === null || value === undefined) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}